import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { stripMarkdown } from '../utils/stripMarkdown';
import { PaperAirplaneIcon, MicrophoneIcon, StopCircleIcon } from '@heroicons/react/24/outline';
import MarkdownMessage from '../components/MarkdownMessage';
import VoiceModeOverlay from '../components/VoiceModeOverlay';
import './ChatWidget.css';

function ChatWidget() {
  const { id } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [chatbotConfig, setChatbotConfig] = useState({});
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  const [recording, setRecording]       = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking]         = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef   = useRef([]);
  const currentAudioRef  = useRef(null);
  const isRecordingRef   = useRef(false);
  const audioCtxRef      = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const storageKey = `chatconv_widget_${id}`;
  const sessionKey = `chatses_widget_${id}`;
  const sessionId = localStorage.getItem(sessionKey) || (() => {
    const sid = 'widget-' + Math.random().toString(36).slice(2);
    localStorage.setItem(sessionKey, sid);
    return sid;
  })();

  const saveConversationId = (convId) => {
    setConversationId(convId);
    if (convId) localStorage.setItem(storageKey, String(convId));
  };

  useEffect(() => {
    axios.get(`/api/chat/${id}/info`)
      .then(r => { setChatbot(r.data.chatbot); setChatbotConfig(r.data.chatbot.config || {}); restoreSession(); })
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const restoreSession = async () => {
    const savedId = localStorage.getItem(storageKey);
    if (!savedId) return;
    try {
      const res = await axios.get(`/api/chat/${id}/conversation/${savedId}`);
      const history = res.data.messages;
      if (history.length > 0) {
        setMessages(history.map(m => ({ role: m.role, content: m.content })));
        setConversationId(parseInt(savedId, 10));
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  };

  const handleNewChat = () => {
    localStorage.removeItem(storageKey);
    setConversationId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const voiceEnabled = chatbotConfig?.voice_enabled === true;
  const ttsProvider  = chatbotConfig?.tts_provider || 'browser';

  const speakResponse = async (text) => {
    if (ttsProvider === 'none' || !text) return;
    window.speechSynthesis?.cancel();
    currentAudioRef.current?.pause();
    if (ttsProvider === 'openai') {
      try {
        setSpeaking(true);
        const res = await fetch(`/api/voice/${id}/speak`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text, voice: chatbotConfig?.tts_voice || 'nova', speed: chatbotConfig?.tts_speed || 1.0 }),
        });
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        const audio = new Audio(URL.createObjectURL(blob));
        currentAudioRef.current = audio;
        audio.onended = () => setSpeaking(false);
        audio.play();
      } catch { setSpeaking(false); }
    } else {
      const utt = new SpeechSynthesisUtterance(text);
      utt.rate = chatbotConfig?.tts_speed || 1.0;
      utt.onstart = () => setSpeaking(true);
      utt.onend   = () => setSpeaking(false);
      window.speechSynthesis.speak(utt);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType =
        MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
        MediaRecorder.isTypeSupported('audio/webm')             ? 'audio/webm' :
        MediaRecorder.isTypeSupported('audio/mp4')              ? 'audio/mp4' :
        MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')  ? 'audio/ogg;codecs=opus' :
        '';

      // ── Web Audio VAD ──────────────────────────────────────────────────
      const audioCtx = new AudioContext();
      audioCtxRef.current = audioCtx;
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      audioCtx.createMediaStreamSource(stream).connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);

      const SPEECH_THRESHOLD  = 20;
      const SILENCE_THRESHOLD = 15;
      const SILENCE_DURATION  = 2000;
      const MIN_RECORD_MS     = 1000;
      const MAX_RECORD_MS     = 60000;
      let silenceStart   = null;
      let speechDetected = false;
      const startedAt    = Date.now();

      // ── MediaRecorder ──────────────────────────────────────────────────
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        audioCtx.close();
        const actualMime = recorder.mimeType || mimeType || 'audio/webm';
        transcribeAudio(new Blob(audioChunksRef.current, { type: actualMime }));
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      isRecordingRef.current   = true;
      setRecording(true);

      // ── Silence detection loop ─────────────────────────────────────────
      const checkSilence = () => {
        if (!isRecordingRef.current) return;
        analyser.getByteFrequencyData(freqData);
        const rms = Math.sqrt(freqData.reduce((s, v) => s + v * v, 0) / freqData.length);
        const now = Date.now();
        if (now - startedAt >= MAX_RECORD_MS) { stopRecording(); return; }
        if (rms >= SPEECH_THRESHOLD) {
          speechDetected = true;
          silenceStart   = null;
        } else if (speechDetected && rms < SILENCE_THRESHOLD) {
          if (!silenceStart) silenceStart = now;
          else if (now - silenceStart >= SILENCE_DURATION && now - startedAt >= MIN_RECORD_MS) {
            stopRecording(); return;
          }
        } else {
          silenceStart = null;
        }
        requestAnimationFrame(checkSilence);
      };
      requestAnimationFrame(checkSilence);

    } catch (err) {
      alert('Could not start recording: ' + (err.message || 'Unknown error') + '\nPlease allow microphone access in your browser settings.');
    }
  };

  const stopRecording = () => {
    isRecordingRef.current = false;
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    const controller = new AbortController();
    const timeoutId  = setTimeout(() => controller.abort(), 25000);
    try {
      const fd = new FormData();
      const ext = (blob.type || 'audio/webm').split('/')[1]?.split(';')[0] || 'webm';
      fd.append('audio', blob, `recording.${ext}`);
      const res  = await fetch(`/api/voice/${id}/transcribe`, { method: 'POST', body: fd, signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Transcription failed');
      if (data.text?.trim()) {
        handleSend(null, data.text.trim());
      } else {
        alert("Couldn't hear anything — please try again.");
      }
    } catch (err) {
      clearTimeout(timeoutId);
      alert(err.name === 'AbortError' ? 'Transcription timed out — please try again.' : 'Transcription failed: ' + err.message);
    } finally {
      setTranscribing(false);
    }
  };

  const handleSend = async (e, forcedText = null) => {
    if (e) e.preventDefault();
    const text = forcedText || input.trim();
    if (!text || sending) return;
    if (!forcedText) setInput('');
    setSending(true);
    setMessages(prev => [...prev, { role: 'user', content: text }]);

    try {
      const res = await axios.post(`/api/chat/${id}`, {
        message: text,
        conversation_id: conversationId,
        user_session_id: sessionId,
      });
      saveConversationId(res.data.conversation_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.message }]);
      if (forcedText) speakResponse(stripMarkdown(res.data.message));
    } catch {
      setMessages(prev => [...prev, { role: 'error', content: 'Something went wrong. Please try again.' }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="widget">
      {voiceModeOpen && (
        <VoiceModeOverlay
          chatbotId={id}
          chatbotName={chatbot?.name || 'Assistant'}
          chatbotConfig={chatbotConfig}
          conversationId={conversationId}
          sessionId={sessionId}
          onConversationId={saveConversationId}
          onClose={() => setVoiceModeOpen(false)}
        />
      )}
      <div className="widget-header">
        <div className="widget-avatar">{chatbot?.name?.[0]?.toUpperCase() || '🤖'}</div>
        <div className="widget-title">
          <span>{chatbot?.name || 'AI Assistant'}</span>
          <span className="widget-status"><span className="w-dot" />Online</span>
        </div>
        <div className="widget-header-actions">
          {voiceEnabled && (
            <button className="widget-voice-mode" onClick={() => setVoiceModeOpen(true)} title="Voice mode">
              🎙
            </button>
          )}
          {messages.length > 0 && (
            <button className="widget-new-chat" onClick={handleNewChat} title="Start a new conversation">
              + New
            </button>
          )}
          <button
            className="widget-close-btn"
            title="Close chat"
            onClick={() => window.parent.postMessage({ type: 'SOLVI_CLOSE' }, '*')}
          >
            ✕
          </button>
        </div>
      </div>

      <div className="widget-messages">
        {/* Welcome message — shown only at the start of a fresh conversation.
            Uses config.welcome_message if set, otherwise falls back to a
            default greeting so it always shows even on unsaved/old chatbots. */}
        {messages.length === 0 && chatbot && (() => {
          const msg = chatbotConfig?.welcome_message !== undefined
            ? chatbotConfig.welcome_message   // explicit value ('' = intentionally hidden)
            : `Hi! I'm ${chatbot.name || 'your assistant'}. 👋 Feel free to look around and ask me anything!`;
          return msg ? (
            <div className="wm wm-assistant wm-welcome">
              <div className="wm-bubble">{msg}</div>
            </div>
          ) : null;
        })()}
        {messages.map((m, i) => (
          <div key={i} className={`wm wm-${m.role}`}>
            <div className="wm-bubble">
              {m.role === 'assistant'
                ? <MarkdownMessage content={m.content} />
                : m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="wm wm-assistant">
            <div className="wm-bubble wm-typing"><span /><span /><span /></div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="widget-input-wrap">
        {(recording || transcribing || speaking) && (
          <div className="w-voice-status">
            {recording    && <><span className="w-voice-dot" />Listening…</>}
            {transcribing && <><span className="w-voice-dot w-vd-yellow" />Transcribing…</>}
            {speaking     && <><span className="w-voice-dot w-vd-green" />Speaking…</>}
          </div>
        )}
        <form className="widget-input" onSubmit={handleSend}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={recording ? 'Listening…' : 'Type a message…'}
            disabled={sending || transcribing}
            autoFocus
          />
          {voiceEnabled && (
            <button
              type="button"
              className={`w-mic-btn ${recording ? 'w-mic-recording' : ''} ${transcribing ? 'w-mic-busy' : ''}`}
              onClick={recording ? stopRecording : startRecording}
              disabled={transcribing || sending || speaking}
              title={recording ? 'Stop' : 'Voice input'}
            >
              {transcribing
                ? <span className="w-mic-spinner" />
                : recording
                  ? <StopCircleIcon style={{ width: 16, height: 16, color: '#ef4444' }} />
                  : <MicrophoneIcon style={{ width: 16, height: 16, color: '#64748b' }} />}
            </button>
          )}
          <button type="submit" disabled={!input.trim() || sending}>
            <PaperAirplaneIcon className="wi-icon" />
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatWidget;
